import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../i18n/translations';
import { useConfig } from '../config/useConfig';

const Gallery = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const { language } = useLanguage();
  const t = translations[language].gallery;
  const config = useConfig();

  const altTexts = [
    'Gothic sci-fi altar with gold circuitry and volumetric light',
    'Laser harp in cyan beams with holy mist and cinematic depth',
    'Fractured stained glass reflecting code in sacred ambience',
    'Ethereal choir in volumetric lighting with golden particles',
    'Endless pale horizon breathing light and cosmic echoes',
    'Cathedral of sound with burning eclipse overhead',
    'Divine fire consuming sacred geometry in darkness',
    'Resonant frequencies visualized in cyan and gold waves',
    'Emerald fractal temple growing from ashes of the old world',
  ];

  const images = config.images.gallery.map((url, index) => ({
    url,
    alt: altTexts[index] || `Gallery image ${index + 1}`,
  }));

  const openLightbox = (index) => {
    setCurrentImage(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  };

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  return (
    <section id="gallery" className="relative py-24 sm:py-32 px-4 sm:px-6 lg:px-8">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/3 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-primary text-sm font-semibold tracking-widest uppercase">{t.subtitle}</span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-2 mb-6">
            <span className="text-glow-soul" style={{ color: 'hsl(186 100% 42%)' }}>{t.title}</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.description}
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map((image, index) => (
            <Card
              key={index}
              className="group relative overflow-hidden bg-card/30 backdrop-blur border-border/50 cursor-pointer aspect-square"
              onClick={() => openLightbox(index)}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-0 group-hover:opacity-80 transition-opacity" />
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-primary/50 transition-all" />
            </Card>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-xl flex items-center justify-center p-4">
          {/* Close Button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 z-50 text-foreground hover:text-primary"
            onClick={closeLightbox}
            aria-label="Close lightbox"
          >
            <X className="h-8 w-8" />
          </Button>

          {/* Navigation */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-foreground hover:text-primary"
            onClick={prevImage}
            aria-label="Previous image"
          >
            <ChevronLeft className="h-8 w-8" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-foreground hover:text-primary"
            onClick={nextImage}
            aria-label="Next image"
          >
            <ChevronRight className="h-8 w-8" />
          </Button>

          {/* Image */}
          <div className="max-w-6xl max-h-[90vh] w-full">
            <img
              src={images[currentImage].url}
              alt={images[currentImage].alt}
              className="w-full h-full object-contain"
            />
            <p className="text-center text-muted-foreground mt-4">
              {currentImage + 1} / {images.length}
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;